import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import AccountingDetailsFormSection from "~/components/formSections/AccountingDetailsFormSection";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import CommentFormSection from "~/components/formSections/CommentFormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_ID } from "~/constants/form.ts";
import useCountrySelectOptions from "~/hooks/form/formOptionsData/useCountrySelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useClientValidationComparisonData,
} from "~/hooks/form/validationComparisonData/useClientValidationComparisonData.ts";
import { createClient } from "~/modules/client/api/client/clientApiDispatchers.ts";
import { useAllClients } from "~/modules/client/api/client/clientQueries.ts";
import type { CreateClientData } from "~/modules/client/api/client/clientTypes.ts";
import { useAllCompanies } from "~/modules/client/api/company/companyQueries.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	onSuccess: (newCompanyId: string) => void;
	companyId: string;
};

interface CreateClientFormData extends Omit<CreateClientData, "paymentTargetDays" | "companyId"> {
	paymentTargetDays: number;
	paymentTargetDaysCustom?: number | null;
}

const CreateClientForm: React.FC<Props> = ({ onSuccess, companyId }) => {
	//const { t } = useTranslation();
	const [busy, setBusy] = useState(false);

	const { data: allCompaniesData } = useAllCompanies();
	const {data: allClientsData} = useAllClients();

	const companyData = useMemo(() => {
		if (allCompaniesData) {
			return allCompaniesData.find((company) => company.id === companyId);
		}
	}, [allCompaniesData, companyId]);

	const clientAddressData = useMemo(() => {
	    if(allClientsData){
			const clientData = allClientsData.filter((client) => client.companyId === companyId).sort(byObjectProperty('id'));
			if(clientData.length > 0){
				return clientData[0];
			}
	    }
	    return null
	    }, [allClientsData, companyId]);

	const displayNameComparisonData = useClientValidationComparisonData();

	const schema = useMemo(() => {
		return yup.object({
			city: yup.string().default(null),
			comment: yup.string().default(null),
			countryId: yup.string().default(null),
			displayName: yup.string().required().unique(displayNameComparisonData),
			houseNumber: yup.string().default(null),
			invoiceByMail: yup.boolean().required(),
			isVatExempt: yup.boolean().required(),
			paymentTargetDays: yup.number().required(),
			paymentTargetDaysCustom: yup.number()
				.nullable()
				.when('paymentTargetDays', (field1Value:number[], schema) => {
					return field1Value[0] === 0
						? schema.required()
						: schema.nullable();
				}),
			street: yup.string().default(null),
			travelExpensesIncluded: yup.boolean().required(),
			zipCode: yup.string().default(null),
		});
	}, [displayNameComparisonData]);

	const defaultValues = useMemo(() => {
		return {
			city: clientAddressData?.city || "",
			comment: "",
			countryId: DEFAULT_COUNTRY_ID,
			displayName: "",
			houseNumber: clientAddressData?.houseNumber || "",
			invoiceByMail: true,
			isVatExempt: false,
			paymentTargetDays: 30,
			paymentTargetDaysCustom: null,
			street: clientAddressData?.street || "",
			travelExpensesIncluded: true,
			zipCode: clientAddressData?.zipCode || "",
		};
	}, [clientAddressData]);
	const {
		handleSubmit,
		control,
		watch,
		formState: { isValid, isDirty, isSubmitted },
		trigger
	} = useForm<CreateClientFormData>({
		defaultValues,
		resolver: yupResolver<CreateClientFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: busy });

	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;

	const countrySelectOptions = useCountrySelectOptions();

	const onSubmit: SubmitHandler<CreateClientFormData> = async (data) => {
		setBusy(true);
		const { paymentTargetDays, paymentTargetDaysCustom, ...rest } = data;
		const paymentTargetDaysValue =
			paymentTargetDays === 0 && paymentTargetDaysCustom ? paymentTargetDaysCustom : paymentTargetDays;

		const newClientData = { paymentTargetDays: paymentTargetDaysValue, companyId, ...rest };

		try {
			const newClientResponse = await createClient(newClientData);
			const clientId = getDataFromResponse(newClientResponse).id;
			onSuccess(clientId);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger('paymentTargetDaysCustom');
		}
	}, [showCustomPaymentTargetInput, trigger]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Neue Firma für Kunden &quot;{companyData?.displayName}&quot; anlegen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<FormSection title="Firma">
						<div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-6">
							<div className="col-span-full sm:col-start-1">
								<Input
									name="displayName"
									label="Name Tochterfirma"
									control={control}
									placeholder="Name der Legaleinheit - bspw. Muster GmbH" />
							</div>
						</div>
					</FormSection>

					<AddressFormSection<CreateClientFormData>
						control={control}
						countrySelectOptions={countrySelectOptions}
					/>

					<AccountingDetailsFormSection<CreateClientFormData>
						control={control}
						showCustomPaymentTargetInput={showCustomPaymentTargetInput}
					/>

					<CommentFormSection
						control={control}
						label="Kommentar"
						title="Kommentar"
						placeholder="Hier können allgemeine Anmerkungen für die Rechnungserstellung hinterlegt werden."
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
			</SidebarFooter>
		</form>
	);
};

export default CreateClientForm;
